import React from "react";
import Marquee from "react-fast-marquee";
import { getLine } from "@/lib/lines";

/**
 * Modern NSW / Sydney Trains LCD-style PID.
 * Deep navy background, structured layout, line-coloured badge & platform.
 */
export default function ModernLCDDisplay({
  destination,
  departureTime,
  platform,
  trainType,
  stops,
  announcement,
  lineCode,
  bookingRequired,
  showFareDuration,
  fare,
  duration,
}) {
  const hasAnnouncement = announcement && announcement.trim().length > 0;
  const trimmedStops = (stops || []).filter(Boolean);
  const line = getLine(lineCode);
  const hasFareInfo = showFareDuration && (fare?.trim() || duration?.trim());

  return (
    <div
      data-testid="modern-lcd-display"
      className="relative w-full h-full overflow-hidden select-none font-lcd"
      style={{ backgroundColor: "#001A33", color: "#FFFFFF" }}
    >
      {/* Top status bar */}
      <div
        className="flex items-center justify-between px-[clamp(10px,2vw,28px)] py-[clamp(6px,1.2vw,16px)]"
        style={{ borderBottom: "2px solid #003B66" }}
      >
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <div
            className="flex items-center justify-center font-bold shrink-0"
            style={{
              backgroundColor: line.color,
              color: line.textColor,
              width: "clamp(30px,5vw,72px)",
              height: "clamp(30px,5vw,72px)",
              borderRadius: "9999px",
              fontSize: "clamp(13px,2.4vw,34px)",
            }}
            data-testid="lcd-line-code"
          >
            {line.code}
          </div>
          <div
            className="uppercase tracking-wider truncate"
            style={{ fontSize: "clamp(11px,1.6vw,22px)", color: "#FFCC00" }}
            data-testid="lcd-train-type"
          >
            {trainType || "All Stations"}
          </div>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 uppercase tracking-widest shrink-0">
          {bookingRequired ? (
            <>
              <span
                className="flex items-center gap-1.5 px-1.5 sm:px-2 py-0.5 font-bold"
                style={{
                  backgroundColor: "#FFCC00",
                  color: "#001A33",
                  fontSize: "clamp(9px,1.2vw,16px)",
                }}
                data-testid="lcd-booking-badge"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="w-3 h-3 sm:w-3.5 sm:h-3.5"
                  aria-hidden="true"
                >
                  <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Zm2 4v2h8V8H6Zm0 4v2h6v-2H6Zm0 4v2h10v-2H6Zm12-8v10a1 1 0 0 0 2 0V8h-2Z" />
                </svg>
                Booking req
              </span>
            </>
          ) : (
            <>
              <span
                className="inline-block rounded-full pulse-dot"
                style={{ width: 8, height: 8, backgroundColor: "#22C55E" }}
              />
              <span style={{ fontSize: "clamp(9px,1.2vw,16px)", color: "#9FD3FF" }}>
                ON TIME
              </span>
            </>
          )}
        </div>
      </div>

      {/* Hero row: destination + platform & time */}
      <div
        className="grid gap-[clamp(8px,1.6vw,24px)] px-[clamp(10px,2vw,28px)] py-[clamp(10px,2vw,30px)]"
        style={{ gridTemplateColumns: "1fr auto" }}
      >
        <div className="flex flex-col justify-center min-w-0">
          <div
            className="uppercase tracking-[0.25em] sm:tracking-[0.35em]"
            style={{ fontSize: "clamp(9px,1.2vw,16px)", color: "#7FB3DA" }}
          >
            Next Service
          </div>
          <div
            className="font-bold leading-[0.95] truncate"
            style={{ fontSize: "clamp(26px,7vw,108px)" }}
            data-testid="lcd-destination"
          >
            {destination || "Set destination"}
          </div>
          {hasFareInfo && (
            <div
              className="flex items-center gap-3 sm:gap-4 mt-[clamp(4px,0.8vw,10px)]"
              style={{ fontSize: "clamp(10px,1.5vw,20px)" }}
              data-testid="lcd-fare-duration"
            >
              {duration?.trim() && (
                <span className="flex items-center gap-1.5 uppercase tracking-widest text-white/80">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3 sm:w-4 sm:h-4 shrink-0">
                    <path d="M12 22a10 10 0 1 1 0-20 10 10 0 0 1 0 20Zm0-18a8 8 0 1 0 0 16 8 8 0 0 0 0-16Zm.5 4v4.3l3.3 2-.8 1.3-4-2.4V8h1.5Z" />
                  </svg>
                  {duration.trim()}
                </span>
              )}
              {fare?.trim() && (
                <span className="flex items-center gap-1.5 uppercase tracking-widest" style={{ color: "#FFCC00" }}>
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3 sm:w-4 sm:h-4 shrink-0">
                    <path d="M12 22a10 10 0 1 1 0-20 10 10 0 0 1 0 20Zm-.9-4V16a5 5 0 0 1-2.6-1.1l.9-1.7A4 4 0 0 0 12 14c.9 0 1.7-.4 1.7-1.2 0-.7-.5-1-1.9-1.4-2-.5-3.1-1.3-3.1-3 0-1.5 1-2.6 2.5-2.9V4h1.8v1.4A4.4 4.4 0 0 1 15 6.4l-.8 1.7a4 4 0 0 0-2.3-.7c-1.2 0-1.6.5-1.6 1.1 0 .6.5.9 2 1.3 2 .5 3 1.4 3 3.1 0 1.6-1.1 2.7-2.7 3V16h-1.5Z" />
                  </svg>
                  {fare.trim()}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col items-end justify-center">
          <div
            className="uppercase tracking-[0.25em] sm:tracking-[0.35em]"
            style={{ fontSize: "clamp(9px,1.2vw,16px)", color: "#7FB3DA" }}
          >
            Departs
          </div>
          <div
            className="font-bold leading-none"
            style={{ fontSize: "clamp(26px,6vw,92px)", color: "#FFCC00" }}
            data-testid="lcd-departure"
          >
            {departureTime || "--:--"}
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 mt-[clamp(4px,1vw,12px)]">
            <div
              className="uppercase tracking-[0.25em] sm:tracking-[0.35em]"
              style={{ fontSize: "clamp(9px,1.2vw,16px)", color: "#7FB3DA" }}
            >
              Platform
            </div>
            <div
              className="font-bold leading-none px-2 sm:px-3 py-0.5 sm:py-1"
              style={{
                fontSize: "clamp(16px,3vw,44px)",
                backgroundColor: line.color,
                color: line.textColor,
              }}
              data-testid="lcd-platform"
            >
              {platform || "–"}
            </div>
          </div>
        </div>
      </div>

      {/* Stops list */}
      <div
        className="px-[clamp(10px,2vw,28px)] py-[clamp(6px,1.4vw,18px)]"
        style={{
          borderTop: "1px solid #0088CC",
          borderBottom: "1px solid #003B66",
        }}
      >
        <div
          className="uppercase tracking-[0.25em] sm:tracking-[0.35em] mb-1"
          style={{ fontSize: "clamp(9px,1.2vw,16px)", color: "#7FB3DA" }}
        >
          Stopping at
        </div>
        {trimmedStops.length > 0 ? (
          <div
            className="flex flex-wrap gap-x-[clamp(6px,1.4vw,22px)] gap-y-1"
            data-testid="lcd-stops-list"
          >
            {trimmedStops.map((stop, idx) => (
              <span
                key={idx}
                className="flex items-baseline gap-1.5 sm:gap-2"
                style={{ fontSize: "clamp(11px,1.9vw,28px)" }}
              >
                <span style={{ color: "#FFCC00" }}>•</span>
                <span>{stop}</span>
              </span>
            ))}
          </div>
        ) : (
          <div
            style={{ fontSize: "clamp(11px,1.9vw,28px)", color: "#7FB3DA" }}
          >
            Add stations in the control panel →
          </div>
        )}
      </div>

      {/* Scrolling announcement bar */}
      <div
        className="absolute left-0 right-0 bottom-0 py-[clamp(5px,1.2vw,16px)]"
        style={{ backgroundColor: "#FF8C00", color: "#001A33" }}
        data-testid="lcd-announcement-bar"
      >
        {(hasAnnouncement || bookingRequired) ? (
          <Marquee gradient={false} speed={60} pauseOnHover>
            <span
              className="font-bold uppercase tracking-wider pr-32"
              style={{ fontSize: "clamp(10px,1.7vw,24px)" }}
            >
              {bookingRequired && "🎫 BOOKING REQUIRED · RESERVED SEATING · "}
              {hasAnnouncement && `⚠  ${announcement}`}
            </span>
          </Marquee>
        ) : (
          <div
            className="text-center font-bold uppercase tracking-widest"
            style={{ fontSize: "clamp(9px,1.4vw,20px)" }}
          >
            Sydney Trains · stand clear, doors closing
          </div>
        )}
      </div>
    </div>
  );
}
